<template>
  <div v-show="isCustomFieldValid">
    <label
      v-if="!props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>
    </label>
    <q-input
      :id="`${customField?.name}`"
      v-model="localVal"
      :for="`${customField?.name}`"
      :name="`${customField?.name}`"
      :readonly="props.readonly"
      dense
      outlined
      class="tw-w-full tw-bg-white"
      :class="{
        'form-input--error': !!errorMessage && showErrorMessage,
        'textarea-xs': isTextArea,
        'input-xs': !isTextArea,
      }"
      :placeholder="props.readonly ? '' : 'Enter a value'"
      :type="isTextArea ? 'textarea' : 'text'"
      :rows="isTextArea ? '3' : undefined"
      @blur="onBlur"
      @keyup.enter="!isTextArea ? onValueChange() : () => {}"
      @keyup.stop.escape="localVal = props.value || ''"
    />
    <span
      v-if="!!errorMessage && showErrorMessage"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import { CustomField, CustomFieldTextSubType, CustomFieldType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string;
  hideLabel?: boolean;
  readonly?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const isCustomFieldValid = computed(() => {
  return (
    props.customField.type === CustomFieldType.TEXT &&
    (props.customField.subType === CustomFieldTextSubType.SHORT_TEXT ||
      props.customField.subType === CustomFieldTextSubType.LONG_TEXT)
  );
});

const localVal = ref<string>(props.value || '');
watch(
  () => props.value,
  () => {
    localVal.value = props.value || '';
  }
);

const errorMessage = computed(() => {
  if (!localVal.value && props.customField.required) {
    return 'This field is required';
  }
  const { minLength, maxLength } = props.customField;
  const valueLength = localVal.value.length;
  if (!valueLength) return '';
  if (!minLength && !maxLength) {
    return '';
  }
  if (
    minLength &&
    maxLength &&
    (valueLength < minLength || valueLength > maxLength)
  ) {
    return `Must be between ${minLength} and ${maxLength} characters`;
  }
  if (minLength && valueLength < minLength) {
    return `Must be at least ${minLength} characters`;
  }
  if (maxLength && valueLength > maxLength) {
    return `Must be at most ${maxLength} characters`;
  }
  return '';
});

const showErrorMessage = ref(false);

watch(localVal, () => {
  showErrorMessage.value = false;
});

function onBlur() {
  if (props.customField.required && (!localVal.value || !!errorMessage.value)) {
    localVal.value = props.value || '';
    return;
  }
  onValueChange();
}

function onValueChange() {
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (props.value === localVal.value || (!props.value && !localVal.value))
    return;
  emit('value-updated', localVal.value);
}

const isTextArea = computed(() => {
  return props.customField.subType === CustomFieldTextSubType.LONG_TEXT;
});

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
